// Where the operator was headed when the session ran out.
//
// A Slack alert links straight at a record (`/explorer/projects/<id>` from
// `recruiter/alerts.py::_project_link`, `/talent-management/optout-requests`,
// …). Arriving on one with an expired session used to cost the whole link: the
// sign-in panel replaced the page, and Google's callback redirects to the admin
// ROOT — it cannot know the path, because a client-side route never reaches the
// backend — so the operator landed on Home and had to go back to Slack and
// click the same link a second time.
//
// So the SPA remembers the path itself. It is captured in the ONE gesture that
// leaves the app (`signInWithGoogle`), and put back before the router mounts
// (`main.tsx`), which is why there is no flash of Home on the way through.
//
// Three properties keep a remembered link from becoming a trap:
//   - **sessionStorage, not localStorage.** The return belongs to THIS tab's
//     round trip to Google. A second tab must not inherit it, and closing the
//     browser must drop it (same reasoning as `lib/launch.ts`).
//   - **It expires**, on the same 10-minute clock as the backend's PKCE/state
//     cookies (`web/routes/admin_auth.py`), which bound the same round trip.
//   - **It is consumed on read, and only at the bare home path** — the exact
//     thing the OAuth callback redirects to. An abandoned sign-in therefore
//     costs one redirect, once, and never hijacks a later visit.
//
// Only a same-origin absolute path is ever stored or restored (`sanitizeReturnTo`):
// this writes the address bar, so anything else is an open redirect.

const STORAGE_KEY = "recruiter_admin_app_return_to";

/** How long a remembered link stays good. Mirrors the 600s `max_age` on the
 *  backend's PKCE + state cookies: past it, the sign-in round trip this link
 *  belonged to can no longer complete anyway. */
const MAX_AGE_MS = 10 * 60 * 1000;

/** Refuse to store anything longer than a plausible deep link. */
const MAX_PATH_LENGTH = 2048;

interface StoredReturnTo {
  readonly path: string;
  /** Epoch ms, so the freshness check needs no parsing. */
  readonly savedAt: number;
}

/** The slice of `window` this module touches, named so tests can pass a fake
 *  (these tests run in the node environment — there is no DOM). */
export interface ReturnToWindow {
  readonly location: { readonly pathname: string; readonly search: string; readonly hash: string };
  readonly history: { replaceState(data: unknown, unused: string, url: string): void };
}

function safeSessionStorage(): Storage | undefined {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A candidate return path → the path we are willing to put in the address bar,
 * or null.
 *
 * Accepts ONLY a same-origin absolute path: it must start with a single "/" and
 * must not be able to name another host. `//evil.example` is protocol-relative
 * and `/\evil.example` is normalized to that by browsers, so both are rejected;
 * so is anything carrying whitespace or a control character. A bare "/" is
 * rejected too — it is Home, i.e. nothing to restore.
 */
export function sanitizeReturnTo(raw: string): string | null {
  const path = raw.trim();
  if (path.length === 0 || path.length > MAX_PATH_LENGTH) return null;
  if (!path.startsWith("/")) return null;
  if (path.startsWith("//") || path.startsWith("/\\")) return null;
  // No whitespace and no control character: browsers strip some of them while
  // resolving a URL, so a "/\n/evil.example" would resolve as protocol-relative.
  for (const ch of path) {
    const code = ch.codePointAt(0) ?? 0;
    if (code <= 0x20 || code === 0x7f) return null;
  }
  if (path === "/") return null;
  return path;
}

/** True for the page the OAuth callback lands on: the bare admin root, with no
 *  query and no fragment of its own. Anywhere else, the operator navigated
 *  somewhere deliberate and we must not move them. */
function isBareHome(location: ReturnToWindow["location"]): boolean {
  return (
    (location.pathname === "/" || location.pathname === "") &&
    location.search === "" &&
    location.hash === ""
  );
}

/** Remember where we are, on the way out to Google. A no-op for Home (nothing
 *  to return to) and when storage is unavailable (private mode) — the operator
 *  then simply lands on Home, which is today's behaviour. */
export function rememberReturnTo(
  location: ReturnToWindow["location"],
  storage: Storage | undefined = safeSessionStorage(),
  now: Date = new Date(),
): void {
  const path = sanitizeReturnTo(`${location.pathname}${location.search}${location.hash}`);
  if (!storage) return;
  try {
    if (!path) {
      storage.removeItem(STORAGE_KEY);
      return;
    }
    const entry: StoredReturnTo = { path, savedAt: now.getTime() };
    storage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota / private mode — the link is lost, the sign-in still works.
  }
}

/** Forget any remembered link (sign out, so a stale entry cannot bounce the
 *  operator who just left back into the console). */
export function clearReturnTo(storage: Storage | undefined = safeSessionStorage()): void {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    // no-op — see rememberReturnTo.
  }
}

/**
 * Take the remembered link, if one applies here. Always consumes: a link that
 * is stale, malformed, or arrives somewhere other than Home is dropped rather
 * than left to fire on the next visit.
 */
export function consumeReturnTo(
  location: ReturnToWindow["location"],
  storage: Storage | undefined = safeSessionStorage(),
  now: Date = new Date(),
): string | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
    if (raw !== null) storage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null || !isBareHome(location)) return null;

  let entry: unknown;
  try {
    entry = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof entry !== "object" || entry === null) return null;
  const { path, savedAt } = entry as Partial<StoredReturnTo>;
  if (typeof path !== "string" || typeof savedAt !== "number" || !Number.isFinite(savedAt)) {
    return null;
  }
  const age = now.getTime() - savedAt;
  if (age < 0 || age > MAX_AGE_MS) return null;
  return sanitizeReturnTo(path);
}

/**
 * Put the address bar back on the deep link the operator was signing in for.
 *
 * Called from `main.tsx` BEFORE React renders, so the router reads the restored
 * URL when it mounts: `replaceState` rather than `pushState` because the OAuth
 * callback's landing on "/" is not a page anyone should be able to go Back to.
 * Returns the restored path, or null when there was nothing to restore.
 */
export function restoreReturnTo(
  win: ReturnToWindow = window,
  storage: Storage | undefined = safeSessionStorage(),
  now: Date = new Date(),
): string | null {
  const path = consumeReturnTo(win.location, storage, now);
  if (path) win.history.replaceState(null, "", path);
  return path;
}
